import { CardVariant } from "../../../models/CardVariant";
import { ErrataSerialDictionary } from "../../../models/Errata";
import { FormatRulesDictionary, TournamentFormat } from "../../../models/TournamentFormat";
import { TournamentRanking, TournamentState, getDefaultTournamentState } from "../../../models/Tournament";
import { FFDecksV3TournamentDeckQuantity, FFDecksV3TournamentResponseDeck } from "../../../models/FFDecksV3";
import { getDeckStateFromDbDeck } from "../../../components/Deck/DeckBuilderCommon";
import { Deck } from "../../../models/Deck";
import { PriceDictionary } from "../../../models/Price";
import { getFFDecksV3Deck, getFFDecksV3Tournament } from "../../../repository/ffdeckRepository";
import { VariantCache } from "../../../appSlice";

export async function parseV3(
    tournamentUrl: string | null | undefined,
    errataDictionary: ErrataSerialDictionary,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    variantCache: VariantCache,
    variantPriceDictionary?: PriceDictionary | undefined
) {
    // https://ffdecks.com/tournament/store-celebration-angers-luddotrotteur/6237461505114112
    if (tournamentUrl === null || tournamentUrl === undefined || !tournamentUrl.trim()) {
        throw new Error("Could not import tournament. FFDecks tournament URL was not provided.");
    }

    const tournamentState: TournamentState = getDefaultTournamentState();
    tournamentState.decks = [];

    const tournamentId = getFFDecksTournamentIdFromUrl(tournamentUrl);
    if (!tournamentId) {
        throw new Error("Could not import tournament. Could not determine the tournament ID from the URL provided.");
    }

    const tournamentResponse = await getFFDecksV3Tournament(tournamentId);

    if (!tournamentResponse || !tournamentResponse.decks) {
        throw new Error("Could not import tournament. Could not fetch this tournament URL from FFDecks.");
    }

    const ffDecksTournament = tournamentResponse;

    tournamentState.attendees = ffDecksTournament.players;
    tournamentState.description = ffDecksTournament.description;
    tournamentState.format = ffDecksTournament.format.name as TournamentFormat;

    const dateParts = new Date(ffDecksTournament.date).toISOString().split("T");
    tournamentState.date = dateParts[0];
    tournamentState.name = ffDecksTournament.name;
    tournamentState.numberOfDecks = ffDecksTournament.decks.length;

    // Add 1 to progress to represent the tournament download.
    const numberOfDownloads = ffDecksTournament.decks.length + 1;
    const progressPerDownload = 100 / numberOfDownloads;
    let progress = 0;

    for (const ffDecksDeck of ffDecksTournament.decks) {
        const deck = parseFFDecksV3Deck(ffDecksDeck, ffDecksTournament.format.name, ffDecksTournament.name, variantCache);
        const deckState = getDeckStateFromDbDeck(deck, "", variantCache, errataDictionary, formatRules, grandfatheredByOpus);
        tournamentState.decks.push(deckState);
    }

    return tournamentState;
}

export function parseFFDecksV3Deck(
    ffDecksDeck: FFDecksV3TournamentResponseDeck,
    format: string,
    tournamentName: string,
    variantCache: VariantCache
) {
    const deck: Deck = {
        displayName: "",
        element: "",
        elementCount: {
            Dark: 0,
            Earth: 0,
            Fire: 0,
            Ice: 0,
            Light: 0,
            Lightning: 0,
            Water: 0,
            Wind: 0
        },
        elements: [],
        format: format ? format as TournamentFormat : "Standard",
        isComplete: true,
        isPublic: true,
        isTournamentDeck: false,
        name: ffDecksDeck.name,
        notes: "",
        playerName: "",
        profileVariantId: "",
        rank: "",
        serials: [],
        totalCardsBySerial: {},
        totalCardsByVariant: {},
        tournamentName: "",
        tournamentId: "",
        customTags: [],
        needsArchetypeProcessing: false
    }

    for (const card of ffDecksDeck.deckCards) {
        const serial = getFullSerial(card);
        const matching = variantCache.any.all.list.filter(x => x.serial === serial || x.fullSerial === serial || x.altSerial === serial) || [];
        let match = matching.find(x => x.isPrimaryVariant);

        if (!match) {
            match = matching[0];
        }

        if (!match) {
            const error = `Could not import deck. Could not find a matching card for ${serial}.`
            throw new Error(error);
        }

        deck.totalCardsBySerial[match.serial] = card.quantity;
        deck.totalCardsByVariant[match.id] = card.quantity;
        deck.serials.push(match.serial);
    }

    deck.isTournamentDeck = true;
    deck.playerName = ffDecksDeck.tournamentPlayerName ?? "";
    deck.rank = ffDecksDeck.tournamentRank as TournamentRanking ?? "";
    deck.tournamentName = tournamentName ?? "";

    return deck;
}

export async function getDeckStateFromFFDecks(
    deckId: string,
    variantCache: VariantCache
) {
    const deckResponse = await getFFDecksV3Deck(deckId);
    const ffDecksDeck = deckResponse;

    const deck = parseFFDecksV3Deck(
        ffDecksDeck,
        ffDecksDeck.format.name ?? "",
        "",
        variantCache);

    deck.tournamentId = "";
    deck.tournamentName = "";
    deck.rank = "";
    deck.isTournamentDeck = false;

    return deck;
}

export function getFFDecksTournamentIdFromUrl(url: string | null): string | null {
    if (!url || !url?.length) {
        return null;
    }

    // https://ffdecks.com/tournament/2194/Store%20Celebatrion%20%20-%20KartenLounge
    if (!url.includes("ffdecks.com/tournament")) {
        return null;
    }

    const urlParts = url.split("/");
    const tournamentIndex = urlParts.indexOf("tournament");
    if (tournamentIndex === -1) {
        return null;
    }

    const idIndex = tournamentIndex + 1;
    if (idIndex >= urlParts.length) {
        return null;
    }

    return urlParts[idIndex];
}

export function getFFDecksDeckIdFromUrl(url: string | null): string | null {
    if (!url || !url?.length) {
        return null;
    }

    // https://ffdecks.com/tournament/2194/Store%20Celebatrion%20%20-%20KartenLounge
    if (!url.includes("ffdecks.com/deck")) {
        return null;
    }

    const urlParts = url.split("/");
    const deckIndex = urlParts.indexOf("deck");
    if (deckIndex === -1) {
        return null;
    }

    const idIndex = deckIndex + 1;
    if (idIndex >= urlParts.length) {
        return null;
    }

    return urlParts[idIndex];
}

function getFullSerial(card: FFDecksV3TournamentDeckQuantity) {
    const rarity = ffdecksRarityToRarity[card.card.rarity.name];

    return rarity === "PR"
        ? card.card.serial
        : `${card.card.serial}${rarity}`;
}

const ffdecksRarityToRarity: Record<string, string> = {
    "Common": "C",
    "Rare": "R",
    "Hero": "H",
    "Legend": "L",
    "Starter": "S",
    "Promo": "PR"
}